/**
 * Terminal outcomes for backend-executed transfers.
 *
 * Submitting a deposit or withdrawal only queues a row — the 200 means
 * "accepted", not "money moved". Without this, a transfer that fails minutes
 * later (drained gas treasury, underfunded wallet, a bridge rejection that
 * mines as a *successful* transaction) is indistinguishable from one that
 * worked: the user saw a success toast either way.
 *
 * Mounted once in `AppLayout`, so an outcome still lands if the user navigates
 * away from the dialog they submitted from.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useTranslate } from "@tolgee/react";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import {
	invalidateAccountState,
	invalidateWalletBalances,
} from "#/hooks/invalidation-helpers";
import { useWallet } from "#/hooks/use-wallet";
import {
	isTransferPending,
	useWalletTransfers,
} from "#/hooks/use-wallet-transfers";
import { analytics } from "#/lib/analytics";
import type { Transfer } from "#/lib/api/wallet";

export function TransferStatusSync() {
	const { address } = useWallet();
	const { data } = useWalletTransfers();
	const queryClient = useQueryClient();
	const { t } = useTranslate();

	// Status at last observation, scoped to the account it was observed for —
	// switching accounts means a different set of transfers, and `since`
	// marks when we started watching this one.
	const seen = useRef<{
		address: string | null;
		statuses: Map<string, Transfer["status"]>;
		since: number;
	} | null>(null);

	useEffect(() => {
		if (!data) return;

		// First snapshot for this account only seeds state. Reloading the
		// page must not replay outcomes the user already saw days ago.
		const watching = seen.current;
		if (!watching || watching.address !== address) {
			seen.current = {
				address,
				statuses: new Map(data.map((tr) => [tr.id, tr.status])),
				since: Date.now(),
			};
			return;
		}

		const announce = (transfer: Transfer) => {
			const isDeposit = transfer.kind === "hl_deposit";
			const amount = Number(transfer.amount_usd);

			analytics.track("transfer_settled", {
				kind: transfer.kind,
				status: transfer.status === "confirmed" ? "confirmed" : "failed",
				amount_usd: Number.isFinite(amount) ? amount : 0,
				reason: transfer.error ?? undefined,
			});

			if (transfer.status === "confirmed") {
				toast.success(
					t(
						isDeposit
							? "toast.deposit-confirmed"
							: "toast.withdrawal-confirmed",
					),
				);
				// The money moved, so both sides of the balance are stale.
				void invalidateWalletBalances(queryClient, address);
				void invalidateAccountState(queryClient, address);
				return;
			}

			// `transfer.error` is written for operators and can describe our
			// own infrastructure ("treasury ETH insufficient…") — never show
			// it to the user. Keep it for support and analytics only.
			if (transfer.error) {
				console.error("[transfers] failed", {
					id: transfer.id,
					kind: transfer.kind,
					error: transfer.error,
				});
			}
			toast.error(
				t(isDeposit ? "toast.deposit-rejected" : "toast.withdrawal-rejected"),
			);
			// Nothing left the wallet on a failure, but the displayed balance
			// may be stale from an optimistic update — resync it.
			void invalidateWalletBalances(queryClient, address);
		};

		for (const transfer of data) {
			const previous = watching.statuses.get(transfer.id);
			watching.statuses.set(transfer.id, transfer.status);

			if (previous === transfer.status) continue;
			if (isTransferPending(transfer)) continue;

			// A row we've never seen before is only news if it was created
			// after we started watching — otherwise it's history. This covers
			// transfers that fail fast enough to be terminal on first sight.
			if (
				previous === undefined &&
				Date.parse(transfer.created_at) < watching.since
			) {
				continue;
			}

			announce(transfer);
		}
	}, [data, address, queryClient, t]);

	return null;
}
